/**
 * iter63z'' — Poller Cadence config card for Site Admin.
 *
 * Tunes the API-Football background poller without redeploying. Each
 * field can be cleared (left blank) to revert to the env-var default.
 *
 *   - LIVE seconds: cache TTL when a fixture is in/imminent. Floor 30s.
 *   - DARK seconds: cache TTL when nothing is happening soon.
 *   - LIVE lookahead (hours): how far ahead a fixture counts as "live window".
 *
 * Quota math (Pro plan = 7500/day):
 *   1 feed × (12h live + 12h dark)
 *     = 12h × 3600 / live_sec   live calls
 *     + 12h × 3600 / dark_sec   dark calls
 *   At live=120s, dark=3600s → 360 + 12 = 372 calls/day per feed.
 */
import { useEffect, useState } from "react";
import { RefreshCw, Save, RotateCcw, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtSec = (n) => {
    if (n == null) return "—";
    if (n < 60) return `${n}s`;
    if (n < 3600) return `${Math.round(n / 60)}m`;
    return `${Math.round(n / 3600)}h`;
};

const dailyEstimate = (liveSec, darkSec) => {
    if (!liveSec || !darkSec) return null;
    // Assume worst case 12h live window per day, 12h dark.
    const live = Math.round((12 * 3600) / liveSec);
    const dark = Math.round((12 * 3600) / darkSec);
    return live + dark;
};

export default function SiteAdminPollerCadenceCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState({ live_sec: "", dark_sec: "", live_lookahead_h: "" });

    const reload = async () => {
        setLoading(true);
        try {
            const d = await api.siteGetPollerCadence();
            setData(d);
            setDraft({
                live_sec: d.overrides.includes("live_sec") ? String(d.live_sec) : "",
                dark_sec: d.overrides.includes("dark_sec") ? String(d.dark_sec) : "",
                live_lookahead_h: d.overrides.includes("live_lookahead_h") ? String(d.live_lookahead_h) : "",
            });
        } catch (e) {
            toast.error("Failed to load poller cadence");
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const onSave = async () => {
        setSaving(true);
        try {
            const body = {};
            for (const k of ["live_sec", "dark_sec", "live_lookahead_h"]) {
                const v = (draft[k] || "").toString().trim();
                body[k] = v === "" ? null : Number(v);
                if (body[k] !== null && (!Number.isInteger(body[k]) || body[k] <= 0)) {
                    toast.error(`${k} must be a positive integer`);
                    setSaving(false); return;
                }
            }
            const d = await api.siteUpdatePollerCadence(body);
            toast.success("Cadence saved — applies on the next poller wake");
            setData(prev => ({ ...prev, ...d }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const onRevert = async (key) => {
        setDraft(prev => ({ ...prev, [key]: "" }));
    };

    const live = draft.live_sec ? Number(draft.live_sec) : (data?.defaults?.live_sec || 0);
    const dark = draft.dark_sec ? Number(draft.dark_sec) : (data?.defaults?.dark_sec || 0);
    const dailyMax = dailyEstimate(live, dark);

    return (
        <div className="brutal-card p-4" data-testid="poller-cadence-card">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-cyber" /> API-Football Poller Cadence
                </h3>
                <button
                    onClick={reload}
                    disabled={loading}
                    className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                    data-testid="poller-cadence-refresh"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Reload
                </button>
            </div>

            {!data ? (
                <p className="text-xs text-zinc-500">Loading…</p>
            ) : (
                <>
                    <p className="text-xs text-zinc-400 mb-4">
                        Tune how often the poller hits API-Football. Leave a field blank to use the env-var default.
                        Changes apply on the <strong>next</strong> poller wake (within{" "}
                        <span className="font-mono">{data.wake_interval_sec}s</span>).
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <CadenceField
                            label="LIVE seconds"
                            help="Cache TTL during live windows (matches in progress or within lookahead). Floor 30s."
                            value={draft.live_sec}
                            placeholder={String(data.defaults.live_sec)}
                            onChange={(v) => setDraft(p => ({ ...p, live_sec: v }))}
                            onRevert={() => onRevert("live_sec")}
                            isOverridden={data.overrides.includes("live_sec")}
                            current={data.live_sec}
                            testid="cadence-live-sec"
                        />
                        <CadenceField
                            label="DARK seconds"
                            help="Cache TTL when no live activity. Catches schedule changes."
                            value={draft.dark_sec}
                            placeholder={String(data.defaults.dark_sec)}
                            onChange={(v) => setDraft(p => ({ ...p, dark_sec: v }))}
                            onRevert={() => onRevert("dark_sec")}
                            isOverridden={data.overrides.includes("dark_sec")}
                            current={data.dark_sec}
                            testid="cadence-dark-sec"
                        />
                        <CadenceField
                            label="LIVE lookahead (hours)"
                            help="How far ahead a fixture counts as 'in the live window'. Lower = less quota burn pre-game."
                            value={draft.live_lookahead_h}
                            placeholder={String(data.defaults.live_lookahead_h)}
                            onChange={(v) => setDraft(p => ({ ...p, live_lookahead_h: v }))}
                            onRevert={() => onRevert("live_lookahead_h")}
                            isOverridden={data.overrides.includes("live_lookahead_h")}
                            current={data.live_lookahead_h}
                            testid="cadence-lookahead-h"
                        />
                    </div>

                    {dailyMax !== null && (
                        <div className="text-xs text-zinc-400 mb-4 p-2 bg-zinc-950/50 border border-zinc-800 rounded">
                            <strong className="text-zinc-200">Estimated upstream calls per feed/day:</strong>{" "}
                            <span className="font-mono text-cyber">~{dailyMax}</span>{" "}
                            <span className="text-zinc-500">
                                (12h live @ {fmtSec(live)} + 12h dark @ {fmtSec(dark)}). Pro plan: 7500/day.
                            </span>
                        </div>
                    )}

                    <button
                        onClick={onSave}
                        disabled={saving}
                        className="px-3 py-1.5 bg-cyber/20 border border-cyber text-cyber rounded text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-cyber/30 disabled:opacity-50"
                        data-testid="poller-cadence-save"
                    >
                        <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save Cadence"}
                    </button>
                </>
            )}
        </div>
    );
}

function CadenceField({ label, help, value, placeholder, onChange, onRevert, isOverridden, current, testid }) {
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</label>
                {isOverridden && (
                    <button
                        onClick={onRevert}
                        title="Revert to env default"
                        className="text-[9px] text-blaze hover:text-blaze/80 flex items-center gap-1"
                        data-testid={`${testid}-revert`}
                    >
                        <RotateCcw className="w-2.5 h-2.5" /> revert
                    </button>
                )}
            </div>
            <input
                type="number"
                min="1"
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-xs font-mono text-white focus:border-cyber focus:outline-none"
                data-testid={testid}
            />
            <p className="text-[10px] text-zinc-500">
                {help} Current: <span className="font-mono text-zinc-300">{current}</span>
                {isOverridden ? <span className="text-blaze ml-1">(override)</span> : <span className="text-zinc-600 ml-1">(default)</span>}
            </p>
        </div>
    );
}
